import { TreeRow } from "@/components/ui/tree/tree-row";
import { CaptureTreeEntityRevision } from "@faro-lotv/service-wires";
import { Box } from "@mui/system";
import { useCallback, type JSX } from "react";
import type { NodeApi, RowRendererProps } from "react-arborist";

type ScanTreeRowProps = RowRendererProps<CaptureTreeEntityRevision> & {
  /** Callback to notify the parent of a drag start. */
  onDragStart?(node: NodeApi<CaptureTreeEntityRevision>): void;
};

/**
 * This component is used to re-implement multi-selection functionalities of the capture tree view.
 * React-arborist provides multi-selection out of the box, but there is a known bug in that library
 * and the fixing PR is not getting merged. Please see https://faro01.atlassian.net/browse/NRT-2161
 * for more information.
 *
 * @returns A component used to override the selection functionalities of the scan tree view
 */
export function ScanTreeRow({
  node,
  innerRef,
  attrs,
  children,
  onDragStart,
}: ScanTreeRowProps): JSX.Element {
  const onClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.ctrlKey || e.metaKey) {
        if (node.isSelected) {
          node.deselect();
        } else {
          node.selectMulti();
        }
      } else if (e.shiftKey) {
        node.selectContiguous();
      } else {
        node.select();
      }
    },
    [node],
  );

  return (
    <Box
      onDragStart={() => onDragStart?.(node)}
      aria-label="Capture tree item"
      data-testid={`scan-tree-row-${node.data.id}`}
    >
      <TreeRow node={node} innerRef={innerRef} attrs={attrs} onClick={onClick}>
        {children}
      </TreeRow>
    </Box>
  );
}
